/**
 * IdRefSelectValue — adaptive value input for ID fields in FilterRow.
 *
 * Renders a shared Select fed by the cross-module dropdown hook matching
 * the idRef kind. Each kind is a dedicated inner component so only the
 * relevant hook runs (lazy fetching: mounts only when an ID field is picked).
 */
import React from 'react';
import { Select } from '@/shared/components/ui';
import { useContactsDropdown } from '@/modules/contacts/hooks/useContactsDropdown';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { useFunnelsDropdown } from '@/modules/funnels/hooks/useFunnels';
import { usePipelinesDropdown } from '@/modules/helpdesk/pipelines/hooks/usePipelines';
import { StepAutocomplete } from '../StepAutocomplete';
import type { IdRefKind } from '../../types/variables';

export interface IdRefSelectValueProps {
  idRef: IdRefKind;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}

interface InnerProps {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}

const toOptions = (items: Array<{ id: string; name: string }> | undefined) =>
  (items ?? []).map((i) => ({ value: i.id, label: i.name }));

const ContactSelect: React.FC<InnerProps> = ({ value, onChange, disabled }) => {
  const { data } = useContactsDropdown();
  return <Select data-testid="filter-value-contact" value={value} onChange={onChange} options={toOptions(data)} placeholder="Selecione o contato..." disabled={disabled} />;
};

const UserSelect: React.FC<InnerProps> = ({ value, onChange, disabled }) => {
  const { data } = useUsersDropdown({ status: 'active' });
  return <Select data-testid="filter-value-user" value={value} onChange={onChange} options={toOptions(data)} placeholder="Selecione o usuário..." disabled={disabled} />;
};

const TeamSelect: React.FC<InnerProps> = ({ value, onChange, disabled }) => {
  const { data } = useTeamsDropdown();
  return <Select data-testid="filter-value-team" value={value} onChange={onChange} options={toOptions(data)} placeholder="Selecione a equipe..." disabled={disabled} />;
};

const ChannelSelect: React.FC<InnerProps> = ({ value, onChange, disabled }) => {
  const { data } = useChannelsDropdown();
  return <Select data-testid="filter-value-channel" value={value} onChange={onChange} options={toOptions(data)} placeholder="Selecione o canal..." disabled={disabled} />;
};

const FunnelSelect: React.FC<InnerProps> = ({ value, onChange, disabled }) => {
  const { data } = useFunnelsDropdown();
  return <Select data-testid="filter-value-funnel" value={value} onChange={onChange} options={toOptions(data)} placeholder="Selecione o funil..." disabled={disabled} />;
};

const PipelineSelect: React.FC<InnerProps> = ({ value, onChange, disabled }) => {
  const { data } = usePipelinesDropdown();
  return <Select data-testid="filter-value-pipeline" value={value} onChange={onChange} options={toOptions(data)} placeholder="Selecione o pipeline..." disabled={disabled} />;
};

export const IdRefSelectValue: React.FC<IdRefSelectValueProps> = ({ idRef, value, onChange, disabled }) => {
  switch (idRef) {
    case 'contact': return <ContactSelect value={value} onChange={onChange} disabled={disabled} />;
    case 'user': return <UserSelect value={value} onChange={onChange} disabled={disabled} />;
    case 'team': return <TeamSelect value={value} onChange={onChange} disabled={disabled} />;
    case 'channel': return <ChannelSelect value={value} onChange={onChange} disabled={disabled} />;
    case 'funnel': return <FunnelSelect value={value} onChange={onChange} disabled={disabled} />;
    case 'pipeline': return <PipelineSelect value={value} onChange={onChange} disabled={disabled} />;
    case 'step': return <StepAutocomplete value={value} onChange={onChange} disabled={disabled} />;
  }
};

export default IdRefSelectValue;
